import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { socialLinks, instagramPosts } from '../data/mock';
import { motion } from 'framer-motion';
import { Instagram, Facebook, Heart, MessageCircle as CommentIcon } from 'lucide-react';

export const SocialMedia = () => {
  const { t } = useLanguage();

  return (
    <section className="relative py-28 lg:py-36 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-semibold text-[#0D9488] tracking-[0.2em] uppercase mb-4">
              {t('social.tagline')}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#0C4A6E] mb-4">
              {t('social.title')}
            </h2>
            <p className="text-lg text-[#64748B]">
              {t('social.subtitle')}
            </p>
          </motion.div>
        </div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center gap-4 mb-14"
        >
          <a
            href={socialLinks.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#E1306C]/30 hover:shadow-md transition-all duration-300"
          >
            <Instagram className="w-5 h-5 text-[#E1306C]" />
            <span className="text-sm font-medium text-[#334155] group-hover:text-[#E1306C] transition-colors">Instagram</span>
          </a>
          <a
            href={socialLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#1877F2]/30 hover:shadow-md transition-all duration-300"
          >
            <Facebook className="w-5 h-5 text-[#1877F2]" />
            <span className="text-sm font-medium text-[#334155] group-hover:text-[#1877F2] transition-colors">Facebook</span>
          </a>
        </motion.div>

        {/* Instagram Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {instagramPosts.map((post, index) => (
            <motion.a
              key={post.id}
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="group relative aspect-square rounded-2xl overflow-hidden"
            >
              <img
                src={post.image}
                alt="Instagram post"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-4 text-white">
                  <span className="flex items-center gap-1 text-sm">
                    <Heart className="w-4 h-4 fill-white" /> {post.likes}
                  </span>
                  <span className="flex items-center gap-1 text-sm">
                    <CommentIcon className="w-4 h-4" /> {post.comments}
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
